import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import customerService from "services/customerService";

// List Customer
export const useListCustomer = (payload, onSuccess, onError, key) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    [key || "customers", params?.page, params?.keywords],
    () => customerService.listCustomer(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("customer"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
          meta: data._meta,
        };
      },
    }
  );
};

// Create Customer
export const useCreateCustomer = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  return useMutation(customerService.createCustomer, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.create", {
          target: t("customer"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("customer"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// useCreateCodeOfCustomer
export const useCreateCodeOfCustomer = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  return useMutation(customerService.createCodeOfCustomer, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.createCodeOfCustomer", {
          target: t("customer"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.createCodeOfCustomer", {
              target: t("customer"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// useGetExportFileFromcreateCodeOfCustomer
export const useGetExportFileFromcreateCodeOfCustomer = (
  onSuccess,
  onError
) => {
  const { t, i18n } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  return useMutation(customerService.getExportFileFromcreateCodeOfCustomer, {
    retry: 0,
    onSuccess: data => {
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.getExportFileFromcreateCodeOfCustomer", {
              target: t("customer"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// useDownloadExportFileFromcreateCodeOfCustomer
export const useDownloadExportFileFromcreateCodeOfCustomer = (
  onSuccess,
  onError
) => {
  const { t, i18n } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  return useMutation(
    customerService.downloadExportFileFromcreateCodeOfCustomer,
    {
      retry: 0,
      onSuccess: data => {
        addToast(
          t("message.downloadExportFileFromcreateCodeOfCustomer", {
            target: t("customer"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      },
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.downloadExportFileFromcreateCodeOfCustomer", {
                target: t("customer"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
    }
  );
};

// Detail Customer
export const useDetailCustomer = (payload, onSuccess, onError) => {
  const { t, i18n } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  const { customerId = null } = payload;

  return useQuery(
    ["customer-detail", customerId],
    () => customerService.detailCustomer(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.detail", {
                target: t("customer"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// Update Customer
export const useUpdateCustomer = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  return useMutation(customerService.updateCustomer, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.update", {
          target: t("customer"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.update", {
              target: t("customer"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Active Customer
export const useActiveCustomer = (onSuccess, onError) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  return useMutation(customerService.activeCustomer, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;

      addToast(
        t("message.active", {
          target: t("customer"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.active", {
              target: t("customer"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};
// Active Customer
export const useActiveIsNotedCustomer = (onSuccess, onError) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  return useMutation(customerService.activeCustomer, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;

      addToast(
        t("message.update", {
          target: t("customer"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.update", {
              target: t("customer"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Delete Customer
export const useDeleteCustomer = (onSuccess, onError) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  return useMutation(customerService.deleteCustomer, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;

      addToast(
        t("message.delete", {
          target: t("customer"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.delete", {
              target: t("customer"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Search Customer
export const useListCustomerByKeySearch = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["customers-by-keyword", params?.keywords],
    () => customerService.listCustomerByKeySearch(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("customer"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
        };
      },
    }
  );
};

// List Customer Schedules
export const useListCustomerSchedules = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["customers-schedules", params?.page, params?.keywords],
    () => customerService.listCustomerSchedules(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("customer"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
        };
      },
    }
  );
};

export const useCreateCustomerSchedules = (onSuccess, onError) => {
  return useMutation(customerService.createCustomerSchedules, {
    retry: 0,
    onSuccess: data => {
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useUpdateCustomerSchedules = (onSuccess, onError) => {
  return useMutation(customerService.updateCustomerSchedules, {
    retry: 0,
    onSuccess: data => {
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useDeleteCustomerSchedules = (onSuccess, onError) => {
  return useMutation(customerService.deleteCustomerSchedules, {
    retry: 0,
    onSuccess: data => {
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// List Customer
export const useListCustomerLocationTransport = (
  payload,
  onSuccess,
  onError
) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  const { params } = payload;

  const queryKey = [
    "customer-location-transport",
    {
      customer_id: params?.columnFilter?.["filter[customer_id]"] ?? null,
      page: params?.page ?? 1,
      size: params?.sizePerPage ?? 10,
      sort: params?.sort ?? null,
      filter: {
        is_new_address:
          params?.columnFilter?.["filter[is_new_address]"] ?? null,
        province_code: params?.columnFilter?.["filter[province_code]"] ?? null,
        district_code: params?.columnFilter?.["filter[district_code]"] ?? null,
        ward_code: params?.columnFilter?.["filter[ward_code]"] ?? null,
        transport_id: params?.columnFilter?.["filter[transport_id]"] ?? null,
        hub_name: params?.columnFilter?.hub_name ?? null,
      },
    },
  ];

  return useQuery(
    queryKey,
    () => customerService.listCustomerLocationTransport(payload),
    {
      enabled: false,
      retry: 1,
      staleTime: 0,
      keepPreviousData: false,
      refetchOnMount: "always",
      placeholderData: undefined,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("customerLocationTransport"),
                status: t("message.error"),
              }),
            t
          ),
          { appearance: "error", autoDismiss: true }
        );
        if (typeof onError === "function") onError(errorResponse);
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
          pageCount: data._meta ? data._meta.pageCount : 0,
        };
      },
    }
  );
};

export const useUpdateCustomerLocationTransport = (onSuccess, onError) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  return useMutation(customerService.updateCustomerLocationTransport, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.update", {
          target: t("customerLocationTransport"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.update", {
              target: t("customerLocationTransport"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useCreateCustomerLocationTransport = (onSuccess, onError) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  return useMutation(customerService.createCustomerLocationTransport, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.create", {
          target: t("customerLocationTransport"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("customerLocationTransport"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useDeleteCustomerLocationTransport = (onSuccess, onError) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();
  return useMutation(customerService.deleteCustomerLocationTransport, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.delete", {
          target: t("customerLocationTransport"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.delete", {
              target: t("customerLocationTransport"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// List Customer
export const useListSaleOfCustomer = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    [
      "customers-sale",
      params?.columnFilter["filter[customer_id]"],
      params?.page,
      params?.keywords,
    ],
    () => customerService.listSaleOfCustomer(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("customerLocationTransport"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
          pageCount: data._meta ? data._meta.pageCount : 0,
        };
      },
    }
  );
};

// useListCodeOfCustomer
export const useListCodeOfCustomer = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    [
      "customers-code",
      params?.columnFilter["filter[customer_id]"],
      params?.page,
      params?.keywords,
    ],
    () => customerService.listCodeOfCustomer(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: "Mã",
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
          pageCount: data._meta ? data._meta.pageCount : 0,
        };
      },
    }
  );
};

// Get Customer Sales by customer ID
export const useGetCustomerSales = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  const { customerId, params } = payload;

  return useQuery(
    ["customer-sales", customerId],
    () => customerService.getCustomerSales({ customerId, params }),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("sale"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
          pageCount: data._meta ? data._meta.pageCount : 0,
        };
      },
    }
  );
};

export const useExportCodeUse = (payload, onSuccess, onError) => {
  const excelParams = {
    "columns[code][dimensionOptions][autoSize]": true,
    "columns[code][label]": "Mã",

    "columns[customer_code][dimensionOptions][autoSize]": true,
    "columns[customer_code][label]": "Mã KH",

    "columns[maker_date][dimensionOptions][autoSize]": true,
    "columns[maker_date][label]": "Ngày tạo",

    "columns[auth_status][dimensionOptions][autoSize]": true,
    "columns[auth_status][label]": "Trạng thái",
    "columns[auth_status][mapping][A]": "Sử dụng",
    "columns[auth_status][mapping][M]": "Chưa sử dụng",
  };
  return useQuery(
    ["customer-code-export"],
    () =>
      customerService.exportCodeUse({
        params: { ...payload.params },
        excelParams: excelParams,
      }),
    {
      enabled: false,
      retry: 1,
      onSuccess: response => {
        onSuccess(response);
      },
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return response;
      },
    }
  );
};
